import { ReactElement, useMemo, useRef, useState } from 'react';
import { CompanyUser, TeamUser } from '@/api';
import { Link, useNavigate } from 'react-router';
import {
    UIAvatar,
    UIEmptyState,
    UIIcon,
    UIMenu,
    UIMenuItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UISection,
    UISectionContent,
    UISectionHeader,
    UITableHeaderCell,
    UIText,
    UITooltip,
    useUIConfirmationModal
} from '@/ui';
import { charcoal, red } from '@/ui/styles';
import { useCompaniesTranslation } from '../hooks';
import { useDeleteTeamUserAction, useUpdateTeamRoleAction } from '../actions';
import { TeamUserRoles } from './team-user-roles';
import { UpdateTeamRoleModal } from './update-team-role-modal';
import { AddUserTeamsModal } from './add-user-teams-modal';
import styles from './member-teams-settings-section.module.scss';
import { uuidv4 } from '@/common/util';

type TeamsSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
};

export function MemberTeamsSettingsSection(
    props: TeamsSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit } = props;

    const { t } = useCompaniesTranslation();

    const [isAddOpen, setIsAddOpen] = useState(false);

    // The Update Role modal derives its openness from a non-null user, so the
    // team being edited is held here and cleared on close.
    const [teamIdToUpdate, setTeamIdToUpdate] = useState<string | null>(null);

    return (
        <>
            <UISection>
                <UISectionHeader
                    title={t('components.member_teams_settings_section.title')}
                    rightButtons={
                        canEdit
                            ? [
                                  {
                                      text: t(
                                          'components.member_teams_settings_section.add_button'
                                      ),
                                      variant: 'textDefault',
                                      type: 'button',
                                      onClick: () => setIsAddOpen(true)
                                  }
                              ]
                            : []
                    }
                />
                {companyUser.teams.length === 0 ? (
                    <UISectionContent>
                        <UIEmptyState
                            description={t(
                                'components.member_teams_settings_section.no_teams'
                            )}
                            emoji="😔"
                        />
                    </UISectionContent>
                ) : (
                    <table className={styles.teamTable}>
                        <thead>
                            <tr>
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_teams_settings_section.team_column'
                                    )}
                                />
                                <UITableHeaderCell
                                    text={t(
                                        'components.member_teams_settings_section.role_column'
                                    )}
                                    tooltip={t(
                                        'components.member_teams_settings_section.role_column_tooltip'
                                    )}
                                />
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {companyUser.teams.map((team) => (
                                <TeamTableRow
                                    key={team.teamId}
                                    team={team}
                                    companyUser={companyUser}
                                    onUpdateRole={() =>
                                        setTeamIdToUpdate(team.teamId)
                                    }
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </UISection>

            <AddUserTeamsModal
                companyUser={companyUser}
                isOpen={isAddOpen}
                onClose={() => setIsAddOpen(false)}
            />

            <UpdateTeamRoleModal
                companyUser={teamIdToUpdate ? companyUser : null}
                teamId={teamIdToUpdate ?? ''}
                onClose={() => setTeamIdToUpdate(null)}
            />
        </>
    );
}

function TeamTableRow(props: {
    team: TeamUser;
    companyUser: CompanyUser;
    onUpdateRole: () => void;
}): ReactElement {
    const { team, companyUser, onUpdateRole } = props;

    const { t } = useCompaniesTranslation();

    // A member can belong to teams the viewer isn't in. Those are still listed
    // — the membership is the point — but they cannot be opened.
    const canView = team.capabilities.canView;
    const teamLink = `/company/${companyUser.companyId}/settings/teams/${team.teamId}`;
    // The whole row is the hover target for the "why not" tooltip, so it is
    // anchored by id rather than wrapping anything.
    const rowId = useMemo(() => `team-row-${uuidv4()}`, []);

    const navigate = useNavigate();
    const popoverRef = useRef<UIPropoverRef>(null);
    const confirmationModal = useUIConfirmationModal();

    // Both actions are team-scoped: their capabilities live on the member's
    // entry for this team, so a team admin of *this* team may act even without
    // company-wide member permissions.
    const updateTeamRoleAction = useUpdateTeamRoleAction({
        user: companyUser,
        teamId: team.teamId
    });
    const deleteTeamUserAction = useDeleteTeamUserAction({
        user: companyUser,
        teamId: team.teamId
    });

    return (
        <tr
            id={canView.allowed ? undefined : rowId}
            className={
                canView.allowed ? styles.tableRow : styles.tableRowLocked
            }
            onClick={
                canView.allowed
                    ? () => {
                          void navigate(teamLink);
                      }
                    : undefined
            }
        >
            <td>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8
                    }}
                >
                    <UIAvatar img={team.image} alt={team.name} size={44} />
                    {canView.allowed ? (
                        <Link to={teamLink} style={{ textDecoration: 'none' }}>
                            <UIText type="link" medium>
                                {team.name}
                            </UIText>
                        </Link>
                    ) : (
                        <>
                            <UIText medium>{team.name}</UIText>
                            <UITooltip
                                anchorId={rowId}
                                text={canView.reason ?? ''}
                                place="top"
                            />
                        </>
                    )}
                </div>
            </td>
            <td>
                <TeamUserRoles roleData={team.roles} />
            </td>
            <td onClick={(e) => e.stopPropagation()}>
                {/* A team the viewer cannot even see has nothing to offer. Once
                    it is visible the menu always shows, with each item saying
                    whether it can be used. */}
                {canView.allowed && (
                    <UIPopover
                        ref={popoverRef}
                        placement="auto"
                        popoverPadding={4}
                        trigger="click"
                        triggerNode={
                            <button className={styles.menuButton}>
                                <UIPressableAreaButtonLess
                                    width={16}
                                    height={16}
                                >
                                    <UIIcon
                                        name={'more-vertical'}
                                        size={16}
                                        color={charcoal}
                                    />
                                </UIPressableAreaButtonLess>
                            </button>
                        }
                    >
                        <UIMenu>
                            <UIMenuItem
                                text={t(
                                    'components.member_teams_settings_section.update_role'
                                )}
                                leftIconName="edit-2"
                                disabled={!updateTeamRoleAction.isAllowed}
                                disabledTooltip={updateTeamRoleAction.reason}
                                onClick={() => {
                                    popoverRef.current?.close();
                                    onUpdateRole();
                                }}
                            />
                            <UIMenuItem
                                text={t(
                                    'components.member_teams_settings_section.remove'
                                )}
                                leftIconName="trash-2"
                                textColor={red}
                                loading={deleteTeamUserAction.loading}
                                disabled={!deleteTeamUserAction.isAllowed}
                                disabledTooltip={deleteTeamUserAction.reason}
                                onClick={() => {
                                    popoverRef.current?.close();
                                    confirmationModal.open({
                                        title: t(
                                            'components.member_teams_settings_section.remove_confirm_title'
                                        ),
                                        confirmText: t(
                                            'components.member_teams_settings_section.remove_confirm_button'
                                        ),
                                        confirmVariant: 'danger',
                                        cancelText: t('shared.labels.cancel'),
                                        onConfirm: () => {
                                            confirmationModal.setLoading(true);
                                            deleteTeamUserAction
                                                .deleteTeamUser()
                                                .catch(() => {}) // Error handled in hook
                                                .finally(() => {
                                                    confirmationModal.setLoading(
                                                        false
                                                    );
                                                    confirmationModal.close();
                                                });
                                        },
                                        onCancel: () => {
                                            confirmationModal.close();
                                        }
                                    });
                                }}
                            />
                        </UIMenu>
                    </UIPopover>
                )}
            </td>
        </tr>
    );
}
